import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Route-level states (spec M0-F7 §6.4, F4 §7.8) and `RelativeTime`.
 *
 * The illustrations are ours by design — monday's artwork may not ship (`[09§B.1 licence]`) —
 * so the VF14 fixture compares layout metrics, not pixels. Open question 104 asks who supplies
 * the final artwork; until then these are simple token-coloured shapes.
 */
import { Skeleton } from '@vibe/core';
import { PRODUCT_NAME } from '../brand/index.js';
/** A neutral placeholder illustration: three board rows fading out. Ours, not monday's. */
function StateIllustration({ tone = 'brand' }) {
    const fill = tone === 'negative' ? 'var(--negative-color, #d83a52)' : 'var(--brand-color, #6161ff)';
    return (_jsxs("svg", { viewBox: "0 0 120 84", width: "120", height: "84", "aria-hidden": "true", focusable: "false", children: [_jsx("rect", { x: "8", y: "8", width: "104", height: "68", rx: "10", fill: "var(--allgrey-background-color, #f6f7fb)" }), _jsxs("g", { fill: fill, children: [_jsx("rect", { x: "22", y: "24", width: "60", height: "8", rx: "4", opacity: "0.9" }), _jsx("rect", { x: "22", y: "40", width: "42", height: "8", rx: "4", opacity: "0.6" }), _jsx("rect", { x: "22", y: "56", width: "52", height: "8", rx: "4", opacity: "0.35" })] })] }));
}
export function PageEmptyState({ title, hint, action, 'data-testid': dataTestId = 'page-empty-state', }) {
    return (_jsxs("div", { className: "ez-page-state", "data-testid": dataTestId, children: [_jsx(StateIllustration, {}), _jsx("h2", { className: "ez-page-state__title", children: title }), hint ? _jsx("p", { className: "ez-page-state__hint", children: hint }) : null, action ? _jsx("div", { className: "ez-page-state__action", children: action }) : null] }));
}
const ERROR_COPY = {
    'not-found': {
        title: "We couldn't find the content you were looking for",
        hint: 'We track these errors automatically, but if the problem reoccurs please contact your administrator.',
    },
    forbidden: {
        title: "You don't have access to this content",
        hint: 'Ask one of its owners for access.',
    },
    error: {
        title: 'Something went wrong',
        hint: `${PRODUCT_NAME} could not load this screen. Retrying usually fixes it.`,
    },
};
export function PageErrorState({ kind = 'error', title, hint, action, 'data-testid': dataTestId = 'page-error-state', }) {
    const copy = ERROR_COPY[kind];
    return (_jsxs("div", { className: "ez-page-state", "data-testid": dataTestId, "data-error-kind": kind, children: [_jsx(StateIllustration, { tone: kind === 'error' ? 'negative' : 'brand' }), _jsx("h2", { className: "ez-page-state__title", children: title ?? copy.title }), _jsx("p", { className: "ez-page-state__hint", children: hint ?? copy.hint }), action ? _jsx("div", { className: "ez-page-state__action", children: action }) : null] }));
}
export function PageSkeleton({ variant = 'page', rows = 10, 'data-testid': dataTestId = 'page-skeleton', }) {
    return (_jsxs("div", { className: "ez-page-skeleton", "data-testid": dataTestId, "data-variant": variant, "aria-busy": "true", children: [variant === 'board' ? (_jsxs("div", { className: "ez-page-skeleton__header", children: [_jsx(Skeleton, { type: "text", size: "h1", width: 280 }), _jsx(Skeleton, { type: "rectangle", width: 420, height: 32 })] })) : (_jsx(Skeleton, { type: "text", size: "h2", width: 220 })), _jsx("div", { className: "ez-page-skeleton__rows", children: Array.from({ length: rows }, (_, index) => (_jsx(Skeleton, { type: "rectangle", height: 36 }, index))) })] }));
}
const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;
/**
 * `5h` / `10M` / `1y` in the activity log, `1 month` / `5 months` in notifications — two
 * different renderings of the same value, both observed, so the format is a prop.
 */
export function relativeTime(value, format, now = Date.now()) {
    const then = value instanceof Date ? value.getTime() : new Date(value).getTime();
    const delta = Math.max(now - then, 0);
    const pick = () => {
        if (delta < MINUTE)
            return [Math.max(Math.floor(delta / 1000), 1), 's', 'second'];
        if (delta < HOUR)
            return [Math.floor(delta / MINUTE), 'm', 'minute'];
        if (delta < DAY)
            return [Math.floor(delta / HOUR), 'h', 'hour'];
        if (delta < MONTH)
            return [Math.floor(delta / DAY), 'd', 'day'];
        if (delta < YEAR)
            return [Math.floor(delta / MONTH), 'M', 'month'];
        return [Math.floor(delta / YEAR), 'y', 'year'];
    };
    const [amount, shortUnit, longUnit] = pick();
    if (format === 'short')
        return `${amount}${shortUnit}`;
    return `${amount} ${longUnit}${amount === 1 ? '' : 's'}`;
}
export function RelativeTime({ value, format = 'short', 'data-testid': dataTestId }) {
    const iso = value instanceof Date ? value.toISOString() : new Date(value).toISOString();
    return (_jsx("time", { className: "ez-relative-time", dateTime: iso, title: iso, "data-testid": dataTestId, children: relativeTime(value, format) }));
}
//# sourceMappingURL=states.js.map